/**
 * Sticky checkout bar (#956).
 *
 * Shown once a plan or add-on change is selected on the plans page or the
 * signup plan step. It summarises the selection (plan, interval, add-ons, the
 * price due now with the regular price struck through) and offers one primary
 * action plus a quiet "Clear selection". It only emits: the page calls its
 * existing checkout or confirm handler, so the bar adds no purchase path and
 * never changes what is charged.
 *
 * It is fixed to the bottom of the main content column (not over the
 * sidebar), sits above the cookie banner while that is open, and lifts the
 * chat bubble above itself, so the three never overlap.
 */
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  PLATFORM_ID,
  Renderer2,
  RendererStyleFlags2,
  inject,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { CheckoutBarSummary, checkoutPrimaryLabel } from '@core/utils/checkout-selection';

/** Body class while a checkout bar is on screen (lifts the chat bubble). */
export const CHECKOUT_BAR_OPEN_CLASS = 'sn-checkout-bar-open';

/** Root CSS variable: space the bar takes at the bottom of the viewport. */
export const CHECKOUT_BAR_SPACE_VAR = '--sn-checkout-bar-space';

@Component({
  selector: 'app-checkout-bar',
  templateUrl: './checkout-bar.component.html',
  styleUrl: './checkout-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CheckoutBarComponent implements OnChanges, AfterViewInit, OnDestroy {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly zone = inject(NgZone);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);

  /** What to show; null hides the bar. */
  @Input() summary: CheckoutBarSummary | null = null;

  /** True while the page's handler runs: the primary button is disabled. */
  @Input() busy = false;

  /** The primary button was pressed. */
  @Output() readonly primary = new EventEmitter<void>();

  /** "Clear selection" was pressed. */
  @Output() readonly clear = new EventEmitter<void>();

  private resizeObserver: ResizeObserver | null = null;
  private mutationObserver: MutationObserver | null = null;
  private readonly onWindowResize = () => this.layout();

  /** The primary button's words for the current mode. */
  get primaryLabel(): string {
    return this.summary ? checkoutPrimaryLabel(this.summary.mode) : '';
  }

  /** Icon for the primary button: a lock for checkout, a chat for a quote. */
  get primaryIcon(): string {
    if (this.summary?.mode === 'quote') return 'fa-comments';
    if (this.summary?.mode === 'change') return 'fa-check';
    return 'fa-lock';
  }

  ngOnChanges(): void {
    // The bar appears and disappears with the summary; re-measure after render.
    if (this.isBrowser) {
      this.zone.runOutsideAngular(() => requestAnimationFrame(() => this.layout()));
    }
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) return;
    this.zone.runOutsideAngular(() => {
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.layout());
        const column = this.column();
        if (column) this.resizeObserver.observe(column);
      }
      const cookie = this.document.querySelector('app-cookie-notification');
      if (cookie && typeof MutationObserver !== 'undefined') {
        this.mutationObserver = new MutationObserver(() => this.layout());
        this.mutationObserver.observe(cookie, { childList: true, subtree: true });
      }
      window.addEventListener('resize', this.onWindowResize, { passive: true });
      this.layout();
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    if (this.isBrowser) {
      window.removeEventListener('resize', this.onWindowResize);
      this.setOpen(false, 0);
    }
  }

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  /** The main content column the bar spans, or null outside the dashboard. */
  private column(): HTMLElement | null {
    return (
      (this.host.nativeElement.closest('main.main-content') as HTMLElement | null) ??
      (this.document.querySelector('main.main-content') as HTMLElement | null)
    );
  }

  /**
   * Places the bar over the content column and above the cookie banner, and
   * publishes the space it takes so the chat bubble and page can make room.
   */
  private layout(): void {
    const el = this.host.nativeElement;
    const bar = el.querySelector('.sn-checkout-bar') as HTMLElement | null;
    if (!bar) {
      this.setOpen(false, 0);
      return;
    }
    const column = this.column();
    const rect = column?.getBoundingClientRect();
    const viewport = this.document.documentElement.clientWidth;
    const left = rect ? Math.max(0, rect.left) : 0;
    const width = rect ? Math.min(rect.width, viewport - left) : viewport;
    const banner = this.document.querySelector('.cookie-banner') as HTMLElement | null;
    const bottom = banner ? banner.getBoundingClientRect().height : 0;

    const flags = RendererStyleFlags2.DashCase;
    this.renderer.setStyle(el, '--cb-left', `${Math.round(left)}px`, flags);
    this.renderer.setStyle(el, '--cb-width', `${Math.round(width)}px`, flags);
    this.renderer.setStyle(el, '--cb-bottom', `${Math.round(bottom)}px`, flags);
    const height = bar.getBoundingClientRect().height;
    this.renderer.setStyle(el, '--cb-height', `${Math.round(height)}px`, flags);
    this.setOpen(true, Math.round(height + bottom));
  }

  private setOpen(open: boolean, space: number): void {
    const body = this.document.body;
    const root = this.document.documentElement;
    if (!body || !root) return;
    if (open) {
      this.renderer.addClass(body, CHECKOUT_BAR_OPEN_CLASS);
      this.renderer.setStyle(root, CHECKOUT_BAR_SPACE_VAR, `${space}px`, RendererStyleFlags2.DashCase);
    } else {
      this.renderer.removeClass(body, CHECKOUT_BAR_OPEN_CLASS);
      this.renderer.removeStyle(root, CHECKOUT_BAR_SPACE_VAR, RendererStyleFlags2.DashCase);
    }
  }
}
